"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  FileText,
  Pencil,
} from "lucide-react";
import {
  CUSTOMER_TYPES,
  CUSTOMER_TYPE_IDS,
} from "@/lib/constants/customer-types";
import { calculateBookingTotals } from "@/lib/pricing/booking-calculations";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { getVenuePaymentTermDays } from "@/lib/constants/accounting";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getCostCentersForOrganization } from "@/lib/constants/accounting-config";
import { useOAuthErrorHandler } from "@/hooks/use-oauth-error-handler";
import OAuthReauthDialog from "@/components/shared/oauth-reauth-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface PaymentInfoCardProps {
  booking: any;
  bookingDetails: any;
  getPaymentIcon: (method: string) => React.JSX.Element;
  onPaymentStatusUpdate?: () => void;
  invoiceData?: any;
  invoiceLoading?: boolean;
  onInvoiceGenerated?: (syncFortnoxStatus?: boolean) => void;
  showInvoiceActions?: boolean; // Control visibility of invoice generation/management actions
  onMarkAsPaidClick?: () => void; // Callback to open mark as paid modal (for bar staff)
  disableActions?: boolean;
  isRoadShow?: boolean;
  venueDefaultLanguage?: string;
  // Which notes API the manual-invoice reason is posted to. The shared
  // /api/bookings/[id]/notes route only admits booking staff/admin, so the bar
  // portal must use its own route or the required note is silently lost.
  notesRoute?: "staff" | "barstaff";
  // The "+N extra participants added by bar staff" line under the total is
  // for booking staff and admins; the bar portal hides it.
  showBarStaffAdditions?: boolean;
}

export function PaymentInfoCard({
  booking,
  bookingDetails,
  getPaymentIcon,
  onPaymentStatusUpdate,
  invoiceData = null,
  invoiceLoading = false,
  onInvoiceGenerated,
  showInvoiceActions = true,
  onMarkAsPaidClick,
  disableActions = false,
  isRoadShow = false,
  venueDefaultLanguage = "en",
  notesRoute = "staff",
  showBarStaffAdditions = true,
}: PaymentInfoCardProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // When true, the next confirm in the modal will both create the invoice
  // *and* ask Fortnox to email the PDF to the customer. When false, the
  // invoice is created as a draft only (current default behaviour).
  const [pendingSendEmail, setPendingSendEmail] = useState(false);
  // Manual invoice flag (mirrors the bar staff drawer flow). Once toggled
  // true, the auto-invoice job will skip this booking — admin then issues
  // the invoice manually outside the accounting integration.
  const [manualInvoiceModalOpen, setManualInvoiceModalOpen] = useState(false);
  const [manualInvoiceNoteDraft, setManualInvoiceNoteDraft] = useState("");
  const [savingManualInvoice, setSavingManualInvoice] = useState(false);
  const [manualInvoiceFlag, setManualInvoiceFlag] = useState<boolean>(
    !!booking?.manual_invoice,
  );
  React.useEffect(() => {
    setManualInvoiceFlag(!!booking?.manual_invoice);
  }, [booking?.manual_invoice]);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const [editedPrice, setEditedPrice] = useState("");
  const [vatPercent, setVatPercent] = useState("25");
  const [priceExcludingVat, setPriceExcludingVat] = useState("");
  // Base price + travel time + extended-staff (preparation) line items
  // (doc §7.5/§8.4; §10.4 preferred: base price is a universal field, default 0,
  // on top of the fixed/per-person amount). UI only for now — folded into the
  // excl-VAT subtotal; no dedicated backend columns yet.
  const [basePrice, setBasePrice] = useState("");
  const [travelCost, setTravelCost] = useState("");
  const [extraStaffCost, setExtraStaffCost] = useState("");

  // Road-show price (doc §7.5): total = base + per_person × participants, plus
  // travel + extended-staff line items. `priceExcludingVat` holds the per-person
  // rate here.
  const rsParticipants = booking.participant_count || 1;
  const rsBasePrice = parseFloat(basePrice) || 0;
  const rsPerPersonRate = parseFloat(priceExcludingVat) || 0;
  const rsPerPersonTotal = rsPerPersonRate * rsParticipants;
  const rsTravelCost = parseFloat(travelCost) || 0;
  const rsExtraStaffCost = parseFloat(extraStaffCost) || 0;
  const rsVatPct = parseFloat(vatPercent) || 0;
  const rsSubtotalExclVat =
    rsBasePrice + rsPerPersonTotal + rsTravelCost + rsExtraStaffCost;
  const rsVatAmount = (rsSubtotalExclVat * rsVatPct) / 100;
  const rsTotalInclVat = rsSubtotalExclVat + rsVatAmount;

  const paymentMethod = booking.payment_method || "card";
  const paymentStatus = booking.payment_status?.name || "Not Paid";
  const currency = bookingDetails?.currency || "SEK";

  // Check if this is a corporate booking
  const isCorporateBooking =
    booking.customer_type_id === CUSTOMER_TYPE_IDS.CORPORATE ||
    booking.customer_type?.name === CUSTOMER_TYPES.CORPORATE;

  const accountingSlug = bookingDetails?.accountings?.name?.toLowerCase();
  const accountingSystemLabel = (() => {
    if (accountingSlug === "poweroffice") return "PowerOffice";
    if (accountingSlug === "fortnox") return "Fortnox";
    if (accountingSlug === "economic") return "e-conomic";
    return "accounting";
  })();

  // Get venue ID for OAuth error handling
  // For road shows, try to get venue from URL
  let venueId =
    booking.studio_show_host_bookings?.[0]?.studios?.venue_id ||
    booking.studio_show_host_bookings?.[0]?.studios?.venues?.id;

  // If no venue ID (road show case), extract from current URL
  if (!venueId || venueId === 0) {
    const pathMatch = typeof window !== 'undefined' ? window.location.pathname.match(/\/venue\/(\d+)\//) : null;
    venueId = pathMatch ? parseInt(pathMatch[1]) : null;
  }

  // Helper function to format date/time based on venue language
  const formatDateTime = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const isNordic = ["sv", "no", "da"].includes(venueDefaultLanguage);

    return dateObj.toLocaleDateString(isNordic ? venueDefaultLanguage : "en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: !isNordic,
    });
  };

  const accountingSystem =
    booking.studio_show_host_bookings?.[0]?.studios?.venues?.accountings
      ?.name || "fortnox";

  // OAuth error handler - only initialize if we have a valid venue ID
  const {
    checkAndHandleOAuthError,
    showReauthDialog,
    setShowReauthDialog,
    accountingSystem: detectedAccountingSystem,
  } = useOAuthErrorHandler({
    venueId: venueId || null,
    defaultAccountingSystem: accountingSystem,
  });

  // Helper function to get cost center name from constants
  const getCostCenterName = () => {
    // For road shows, use venue from bookingDetails; otherwise use from studio relationship
    let venue = isRoadShow
      ? bookingDetails?.venue
      : booking.studio_show_host_bookings?.[0]?.studios?.venues;

    if (
      !venue?.cost_center ||
      !venue?.organization_id ||
      !venue?.accountings?.name
    ) {
      return "N/A";
    }

    const accountingSystem = venue.accountings.name as "fortnox" | "economic";
    const organizationId = venue.organization_id;
    const costCenterId = venue.cost_center;

    try {
      const costCenters = getCostCentersForOrganization(
        accountingSystem,
        organizationId,
      );
      const costCenter = costCenters.find((cc) => cc.id === costCenterId);
      return costCenter?.name || costCenterId; // Return ID as fallback if name not found
    } catch (error) {
      console.error("Error getting cost center name:", error);
      return costCenterId || "N/A";
    }
  };

  // Get the correct base price and VAT info
  let unitPrice: number;
  let vatRate: number;

  if (
    booking.base_amount_excl_vat !== undefined &&
    booking.base_amount_excl_vat !== null
  ) {
    // Use new DB fields
    unitPrice =
      booking.price_type_id === 2
        ? booking.base_amount_excl_vat / (booking.participant_count || 1)
        : booking.base_amount_excl_vat;
    vatRate = booking.vat_rate || 0;
  } else {
    // Fallback to pricing data
    unitPrice = bookingDetails?.pricing?.price || 0;
    vatRate = bookingDetails?.pricing?.vat_percentage || 25;
  }

  // Apply price modifier if exists
  let modifiedUnitPrice = unitPrice;
  if (booking.price_modifier) {
    const modifier = booking.price_modifier;
    if (modifier.modifier_type === "discount") {
      modifiedUnitPrice =
        unitPrice * (1 - Math.abs(modifier.modifier_value) / 100);
    } else if (
      modifier.modifier_type === "surcharge" ||
      modifier.modifier_type === "percentage"
    ) {
      modifiedUnitPrice = unitPrice * (1 + modifier.modifier_value / 100);
    }
  }

  // Use centralized calculation
  const totals = calculateBookingTotals(booking, modifiedUnitPrice, vatRate);

  // Apply the partner-commission discount to the shown totals (it's
  // applied to show fees only — never to add-ons or shop items). The
  // discount is also surfaced as a line in PriceSummaryCard; here we
  // just net it out of the displayed subtotal/grandTotal so the
  // numbers staff see match the eventual invoice total from Fortnox/
  // PowerOffice.
  const partnerCommissionPct =
    booking?.partner_id && booking?.partner_commission_percentage != null
      ? Number(booking.partner_commission_percentage)
      : 0;
  const partnerFraction = partnerCommissionPct / 100;
  const partnerShowsDiscountExclVat =
    (totals?.shows?.baseAmount ?? 0) * partnerFraction;
  const partnerShowsDiscountVat =
    (totals?.shows?.vatAmount ?? 0) * partnerFraction;
  const subtotalNet =
    (totals?.subtotal ?? 0) - partnerShowsDiscountExclVat;
  const grandTotalNet =
    (totals?.grandTotal ?? 0) -
    partnerShowsDiscountExclVat -
    partnerShowsDiscountVat;
  // Always use the grand total that includes add-ons and shop items
  const totalPrice = grandTotalNet;

  const handleMarkAsPaid = async () => {
    setIsUpdating(true);
    try {
      const response = await fetch(
        `/api/bookings/${booking.id}/payment-status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            payment_status_id: STATUS_IDS.PAYMENT_PAID, // 21
            reason: "Marked as paid by admin",
            old_payment_status_id: booking.payment_status_id,
            old_payment_status_name: paymentStatus,
            new_payment_status_name: "Paid",
          }),
        },
      );

      const result = await response.json();

      if (result.success) {
        toast.success("Payment status updated to Paid");
        if (onPaymentStatusUpdate) {
          onPaymentStatusUpdate();
        }
      } else {
        toast.error(result.error || "Failed to update payment status");
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Failed to update payment status");
    } finally {
      setIsUpdating(false);
    }
  };

  // Mark this booking for manual invoice handling. Mirrors the bar staff
  // drawer flow: writes manual_invoice=true on the booking and drops a
  // booking_notes row with category='manual_invoice' so the reason is
  // captured. Two sequential writes — if the note fails after the flag is
  // set, the local flag still flips so staff sees the badge, and the
  // toast surfaces the partial state for retry.
  const handleConfirmManualInvoice = async () => {
    if (!manualInvoiceNoteDraft.trim() || !booking?.id) return;
    if (!venueId) {
      toast.error("Venue ID not available — refresh the page and try again");
      return;
    }

    setSavingManualInvoice(true);
    try {
      const updateRes = await fetch(
        `/api/staff/venues/${venueId}/bookings/${booking.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ manual_invoice: true }),
        },
      );
      if (!updateRes.ok) {
        const err = await updateRes.json().catch(() => ({}));
        throw new Error(err.error || "Failed to mark booking for manual invoice");
      }

      // The two notes routes accept different body keys for the note text
      const noteRes = await fetch(
        notesRoute === "barstaff"
          ? `/api/barstaff/bookings/${booking.id}/notes`
          : `/api/bookings/${booking.id}/notes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...(notesRoute === "barstaff"
              ? { content: manualInvoiceNoteDraft }
              : { note_content: manualInvoiceNoteDraft }),
            category: "manual_invoice",
          }),
        },
      );
      if (!noteRes.ok) {
        const err = await noteRes.json().catch(() => ({}));
        // Flag is already set — reflect that locally so the button switches
        // to "Manual Invoice ✓", then surface the note failure.
        setManualInvoiceFlag(true);
        throw new Error(
          err.error || "Booking marked, but note could not be saved",
        );
      }

      setManualInvoiceFlag(true);
      setManualInvoiceModalOpen(false);
      setManualInvoiceNoteDraft("");
      toast.success("Marked for manual invoice");
    } catch (err) {
      toast.error((err as Error).message || "Something went wrong");
    } finally {
      setSavingManualInvoice(false);
    }
  };

  const handleGenerateInvoice = async () => {
    setIsGeneratingInvoice(true);
    setShowConfirmModal(false); // Close the modal
    const sendEmail = pendingSendEmail;

    try {
      const response = await fetch(`/api/bookings/${booking.id}/invoice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          venueId: venueId, // Pass the venue ID for road shows
          sendEmail,
        }),
      });

      const result = await response.json();

      // Check for OAuth errors first
      if (!result.success && checkAndHandleOAuthError(result.error || result)) {
        // OAuth dialog will show automatically
        return;
      }

      if (result.success) {
        // Distinguish the two flows in the toast so staff knows whether the
        // PDF actually went out. If the email step failed but the invoice
        // itself succeeded, warn instead of erroring.
        if (sendEmail) {
          if (result.emailSent) {
            toast.success("Invoice generated and emailed to customer");
          } else {
            toast.warning(
              `Invoice generated, but email failed: ${
                result.emailError || "unknown reason"
              }`,
              { duration: 7000 },
            );
          }
        } else {
          toast.success("Invoice generated successfully");
        }
      } else {
        // Show detailed error message based on error type
        const errorDetails = result.details;
        const errorMessage = result.error || "Failed to generate invoice";

        // Handle specific error codes with detailed messages
        if (
          errorDetails?.code === "MAPPING_ERROR" &&
          errorDetails?.details?.unmappedItems
        ) {
          const items = errorDetails.details.unmappedItems;
          const itemsList = items
            .map((item: any) => `${item.name} (${item.type})`)
            .join(", ");
          toast.error(
            `Cannot generate invoice: ${itemsList} not mapped to accounting articles`,
            {
              duration: 8000,
            },
          );
        } else if (errorDetails?.code === "VENUE_CONFIGURATION_ERROR") {
          toast.error(errorMessage, {
            duration: 6000,
          });
        } else if (errorDetails?.code === "CUSTOMER_CREATION_FAILED") {
          toast.error(
            `Customer creation failed: ${errorDetails?.details?.message || errorMessage}`,
            {
              duration: 6000,
            },
          );
        } else {
          toast.error(errorMessage, {
            duration: 5000,
          });
        }
      }

      // Always refresh invoice data to show current status (Syncing, Sent to Accounting, or Failed)
      if (onInvoiceGenerated) {
        onInvoiceGenerated();
      }
      if (onPaymentStatusUpdate) {
        onPaymentStatusUpdate();
      }
    } catch (error) {
      console.error("Error generating invoice:", error);

      // Check if it's an OAuth error
      if (!checkAndHandleOAuthError(error)) {
        toast.error("Failed to generate invoice");
      }

      // Refresh to show error status
      if (onInvoiceGenerated) {
        onInvoiceGenerated();
      }
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleDisconnectInvoice = async () => {
    setIsDisconnecting(true);
    try {
      const response = await fetch(`/api/bookings/${booking.id}/invoice`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "disconnect",
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Invoice disconnected successfully");
        setShowDisconnectModal(false);

        // Refresh to show updated state
        if (onInvoiceGenerated) {
          onInvoiceGenerated();
        }
      } else {
        toast.error(data.error || "Failed to disconnect invoice");
      }
    } catch (error) {
      console.error("Error disconnecting invoice:", error);
      toast.error("Failed to disconnect invoice");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleRefreshFortnoxStatus = async () => {
    if (onInvoiceGenerated) {
      toast.info(`Refreshing invoice status from ${accountingSystemLabel}...`);
      await onInvoiceGenerated(true); // Pass true to sync Fortnox status
    }
  };

  const getInvoiceUrl = (invoiceNumber: string) => {
    const accountingSystem = bookingDetails?.accountings?.name?.toLowerCase();

    if (accountingSystem === "poweroffice") {
      // PowerOffice Go has no documented public deep-link to a specific sales order;
      // route the user to the Go application home and they navigate from there.
      const credentials = bookingDetails?.accounting_credentials;
      const isDemo = credentials?.environment === "demo";
      return isDemo
        ? "https://godemo.poweroffice.net/"
        : "https://go.poweroffice.net/";
    }

    const credentials = bookingDetails?.accounting_credentials;
    let fortnoxAppId: string | undefined;
    if (credentials) {
      fortnoxAppId = credentials.appId || credentials.additional_config?.appId;
      if (!fortnoxAppId && typeof credentials.additional_config === "string") {
        try {
          const config = JSON.parse(credentials.additional_config);
          fortnoxAppId = config?.appId;
        } catch (e) {
          console.error("Failed to parse additional_config:", e);
        }
      }
    }

    if (fortnoxAppId) {
      return `https://apps5.fortnox.se/app/${fortnoxAppId}/kf/invoice/${invoiceNumber}`;
    }
    return `https://apps.fortnox.se/invoice/${invoiceNumber}`;
  };

  const getFortnoxStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
        return (
          <Badge variant="default" className="bg-green-500 hover:bg-green-600">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Paid
          </Badge>
        );
      case "sent":
        return (
          <Badge
            variant="secondary"
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <FileText className="w-3 h-3 mr-1" />
            Sent
          </Badge>
        );
      case "overdue":
        return (
          <Badge
            variant="secondary"
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            Overdue
          </Badge>
        );
      case "draft":
        return (
          <Badge
            variant="secondary"
            className="bg-gray-500 hover:bg-gray-600 text-white"
          >
            <Clock className="w-3 h-3 mr-1" />
            Draft
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="outline" className="border-gray-400">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelled
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPaymentStatusBadge = () => {
    switch (paymentStatus.toLowerCase()) {
      case "paid":
        return (
          <Badge variant="default" className="bg-green-500 hover:bg-green-600">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Paid
          </Badge>
        );
      case "invoice sent":
        return (
          <Badge
            variant="secondary"
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <FileText className="w-3 h-3 mr-1" />
            Invoice Sent
          </Badge>
        );
      case "pending":
        return (
          <Badge
            variant="secondary"
            className="bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            <Clock className="w-3 h-3 mr-1" />
            Pending Payment
          </Badge>
        );
      case "not paid":
        return (
          <Badge
            variant="secondary"
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            Not Paid
          </Badge>
        );
      case "refunded":
        return (
          <Badge variant="outline" className="border-gray-400">
            <XCircle className="w-3 h-3 mr-1" />
            Refunded
          </Badge>
        );
      default:
        return <Badge variant="outline">{paymentStatus}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg">Payment information</CardTitle>
          {getPaymentStatusBadge()}
        </div>
        {isRoadShow && !disableActions && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Pre-fill with current booking values. Road-show price is split:
              //  - base fee   → show_base_price
              //  - per-person → per_person_mode_price_excluding_vat
              setBasePrice(
                booking.show_base_price != null
                  ? booking.show_base_price.toString()
                  : "",
              );
              setPriceExcludingVat(
                booking.per_person_mode_price_excluding_vat != null
                  ? booking.per_person_mode_price_excluding_vat.toString()
                  : "",
              );
              setEditedPrice(
                booking.base_amount_excl_vat != null
                  ? (
                      booking.base_amount_excl_vat + (booking.vat_amount || 0)
                    ).toString()
                  : totalPrice.toString(),
              );
              if (booking.vat_rate !== undefined && booking.vat_rate !== null) {
                setVatPercent(booking.vat_rate.toString());
              }
              setTravelCost(
                booking.travel_cost_excl_vat != null
                  ? booking.travel_cost_excl_vat.toString()
                  : "",
              );
              setExtraStaffCost(
                booking.extended_staff_cost_excl_vat != null
                  ? booking.extended_staff_cost_excl_vat.toString()
                  : "",
              );
              setShowEditPriceModal(true);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Payment Preference */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {getPaymentIcon(paymentMethod)}
            <div>
              <p className="text-sm font-medium">Payment preference</p>
              <p className="text-sm text-gray-600 capitalize">
                {paymentMethod || "Not specified"}
              </p>
            </div>
          </div>
          <div className="text-right">
            {isCorporateBooking && paymentMethod === "card" ? (
              <>
                <p className="text-sm font-medium">Total amount (excl. VAT)</p>
                <p className="text-lg font-semibold">
                  {Math.round(subtotalNet).toLocaleString()} {getCurrencySymbol(currency)}
                </p>
                <p className="text-sm font-medium mt-2">Total amount (incl. VAT)</p>
                <p className="text-lg font-semibold">
                  {Math.round(grandTotalNet).toLocaleString()} {getCurrencySymbol(currency)}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">
                  Total amount {isCorporateBooking || isRoadShow ? "(excl. VAT)" : "(incl. VAT)"}
                </p>
                <p className="text-lg font-semibold">
                  {isRoadShow && totalPrice === 0
                    ? `Not set`
                    : // For corporate bookings and road shows, show subtotal (excl VAT)
                      isCorporateBooking || isRoadShow
                    ? `${Math.round(subtotalNet).toLocaleString()} ${getCurrencySymbol(currency)}`
                    : // For private bookings, show grand total including VAT (shows + add-ons + shop items)
                    `${Math.round(grandTotalNet).toLocaleString()} ${getCurrencySymbol(currency)}`}
                </p>
              </>
            )}
            {showBarStaffAdditions && booking.bar_staff_added_participants > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {`+${booking.bar_staff_added_participants} extra participant${
                  booking.bar_staff_added_participants === 1 ? "" : "s"
                } added by bar staff`}
              </p>
            )}
          </div>
        </div>

        {/* Invoice Details for Corporate */}
        {booking.customer_type_id === CUSTOMER_TYPE_IDS.CORPORATE &&
          paymentMethod === "invoice" && (
            <div className="border-t pt-4 space-y-2">
              <h4 className="text-sm font-semibold">Invoice details</h4>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total amount (incl. VAT):</span>
                <span className="text-lg font-semibold">
                  {Math.round(grandTotalNet).toLocaleString()} {getCurrencySymbol(currency)}
                </span>
              </div>
              {booking.invoice_reference && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Invoice reference:
                  </span>
                  <span className="text-sm font-medium">
                    {booking.invoice_reference}
                  </span>
                </div>
              )}
              {booking.cost_center && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Cost center:</span>
                  <span className="text-sm font-medium">
                    {booking.cost_center}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Payment terms:</span>
                <span className="text-sm font-medium">
                  Net{" "}
                  {booking?.partner?.payment_terms_days ??
                    getVenuePaymentTermDays(
                      (
                        booking.studio_show_host_bookings?.[0]?.studios?.venues
                          ?.venue_infos ?? []
                      ).map((v: { name: string }) => v.name),
                    )}{" "}
                  days
                </span>
              </div>
            </div>
          )}

        {/* Payment Actions */}
        {paymentStatus &&
          paymentStatus.toLowerCase() !== "paid" &&
          paymentStatus.toLowerCase() !== "refunded" ? (
            <div className="border-t pt-4 space-y-3">
              {/* Invoice Status Messages */}
              {invoiceData && (
                <div>
                  {/* Syncing Status */}
                  {invoiceData.statuses?.name === "Syncing" && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 flex-shrink-0">
                          <svg
                            className="animate-spin h-4 w-4 text-blue-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-900">
                            Syncing invoice
                          </p>
                          <p className="text-xs text-blue-700 mt-1">
                            Creating invoice in the accounting system...
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sent to Accounting - Invoice Details Section */}
                  {invoiceData.statuses?.name === "Sent to Accounting" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">
                          Invoice details
                        </h4>
                        {showInvoiceActions && (
                          <button
                            onClick={() => setShowDisconnectModal(true)}
                            className="text-red-500 hover:text-red-700"
                            title="Disconnect invoice"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-5 h-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          Total amount (incl. VAT):
                        </span>
                        <span className="text-lg font-semibold">
                          {Math.round(grandTotalNet).toLocaleString()} {getCurrencySymbol(currency)}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            Invoice Number:
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {invoiceData.invoice_id}
                            </span>
                            {/* "View in accounting system" deep-link. e-conomic
                                has no usable per-invoice deep-link (getInvoiceUrl
                                would fall back to a Fortnox URL), so hide the
                                icon for e-conomic venues. */}
                            {accountingSlug !== "economic" && (
                              <a
                                href={getInvoiceUrl(invoiceData.invoice_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                title={`View in ${accountingSystemLabel}`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="w-4 h-4"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                  />
                                </svg>
                              </a>
                            )}
                          </div>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            Invoice Date:
                          </span>
                          <span className="text-sm font-medium">
                            {invoiceData.generated_date &&
                              new Date(
                                invoiceData.generated_date,
                              ).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            Invoice Status:
                          </span>
                          {invoiceData.fortnoxStatus ? (
                            getFortnoxStatusBadge(
                              invoiceData.fortnoxStatus.status,
                            )
                          ) : invoiceData.fortnoxError ? (
                            <span
                              className="text-sm text-gray-500"
                              title={invoiceData.fortnoxError}
                            >
                              -
                            </span>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700 border-blue-200"
                            >
                              Sent to Accounting
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold mb-3">
                          Payment Timeline
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                            <p className="text-sm">
                              <span className="text-gray-600">
                                Booking created:{" "}
                              </span>
                              <span className="font-medium">
                                {formatDateTime(booking.created_at)}
                              </span>
                            </p>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <p className="text-sm">
                              <span className="text-gray-600">
                                Invoice sent to Accounting:{" "}
                              </span>
                              <span className="font-medium">
                                {invoiceData.generated_date &&
                                  formatDateTime(invoiceData.generated_date)}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Creation Failed Status */}
                  {invoiceData.statuses?.name === "Invoice creation Failed" && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-900">
                            Invoice Creation Failed
                          </p>
                          {invoiceData.failure_reason ? (
                            <p className="text-xs text-red-800 mt-1 break-words">
                              {invoiceData.failure_reason}
                            </p>
                          ) : (
                            <p className="text-xs text-red-700 mt-1">
                              Failed to create invoice in the accounting system.
                            </p>
                          )}
                          <p className="text-xs text-red-700 mt-1">
                            Fix the cause and try again, or contact support.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons Row */}
              {!disableActions && (
                <div className="flex gap-2">
                {/* Generate Invoice buttons - For invoice payment method including road shows.
                    Two actions:
                      - Generate Invoice (draft only in accounting system)
                      - Generate & Send (draft + email PDF to customer's invoice_email
                        via the accounting system's send API) */}
                {(showInvoiceActions &&
                  paymentMethod === "invoice" &&
                  booking.payment_status_id !== STATUS_IDS.PAYMENT_PAID &&
                  booking.status_id !== STATUS_IDS.BOOKING_CANCELLED &&
                  !invoiceLoading) ? (
                    <>
                      {/* Show buttons only if no invoice exists, creation failed, or disconnected */}
                      {(!invoiceData ||
                        invoiceData.statuses?.name ===
                          "Invoice creation Failed" ||
                        invoiceData.statuses?.name === "Disconnected") && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              setPendingSendEmail(false);
                              setShowConfirmModal(true);
                            }}
                            disabled={
                              isGeneratingInvoice ||
                              invoiceData?.statuses?.name === "Syncing"
                            }
                          >
                            {invoiceData?.statuses?.name === "Syncing"
                              ? "Syncing"
                              : invoiceData?.statuses?.name ===
                                  "Invoice creation Failed"
                                ? "Retry Invoice Generation"
                                : "Generate Invoice"}
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              setPendingSendEmail(true);
                              setShowConfirmModal(true);
                            }}
                            disabled={
                              isGeneratingInvoice ||
                              invoiceData?.statuses?.name === "Syncing"
                            }
                          >
                            {invoiceData?.statuses?.name === "Syncing"
                              ? "Syncing"
                              : invoiceData?.statuses?.name ===
                                  "Invoice creation Failed"
                                ? "Retry Generate & Send"
                                : "Generate & Send"}
                          </Button>
                        </>
                      )}
                    </>
                  ) : null}

                {/* Other Invoice Actions */}
                {paymentStatus.toLowerCase() === "invoice sent" &&
                  paymentMethod === "invoice" && (
                    <Button variant="outline" size="sm" className="flex-1">
                      Send Payment Reminder
                    </Button>
                  )}

                {/* Mark as Paid - Only for non-invoice payment methods and non-cancelled bookings */}
                {paymentMethod !== "invoice" && booking.status_id !== STATUS_IDS.BOOKING_CANCELLED && (
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={onMarkAsPaidClick || handleMarkAsPaid}
                    disabled={
                      isUpdating || paymentStatus.toLowerCase() === "paid"
                    }
                  >
                    {paymentStatus.toLowerCase() === "paid"
                      ? "Paid"
                      : isUpdating
                        ? "Updating..."
                        : "Mark as Paid"}
                  </Button>
                )}
              </div>
              )}
            </div>
          ) : null}

        {/* Payment Timeline - Only show if invoice status is not "Sent to Accounting" */}
        {invoiceData?.statuses?.name !== "Sent to Accounting" ? (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2 gap-2">
              <h4 className="text-sm font-semibold">Payment Timeline</h4>
              {/* Manual Invoice — flags the booking so the auto-invoice job
                  skips it; admin handles invoicing manually outside the
                  integration. One-way like the bar staff drawer flow.
                  Only relevant for invoice-payment bookings that haven't
                  already been sent to the accounting system. */}
              {!disableActions && paymentMethod === "invoice" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isGeneratingInvoice || manualInvoiceFlag}
                  title={
                    manualInvoiceFlag
                      ? "Already marked for manual invoice"
                      : "Manual Invoice"
                  }
                  onClick={() => setManualInvoiceModalOpen(true)}
                >
                  {manualInvoiceFlag ? "Manual Invoice ✓" : "Manual Invoice"}
                </Button>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span className="text-gray-600">Booking created:</span>
                <span className="font-medium">
                  {formatDateTime(booking.created_at)}
                </span>
              </div>
              {paymentStatus.toLowerCase() === "paid" && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-gray-600">Payment received:</span>
                  <span className="font-medium">
                    {formatDateTime(booking.updated_at)}
                  </span>
                </div>
              )}
              {paymentStatus.toLowerCase() === "invoice sent" &&
                paymentMethod === "invoice" && (
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-600">Invoice sent:</span>
                    <span className="font-medium">
                      {new Date(
                        new Date(booking.created_at).getTime() +
                          24 * 60 * 60 * 1000,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}
            </div>
          </div>
        ) : null}
      </CardContent>

      {/* Generate Invoice Confirmation Modal — title / wording adapts based on
          whether the user clicked Generate Invoice (draft only) or
          Generate & Send (draft + email PDF). */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {pendingSendEmail ? "Generate & Send Invoice" : "Generate Invoice"}
            </DialogTitle>
            <DialogDescription>
              {pendingSendEmail
                ? "An invoice will be created in the accounting system and emailed to the customer as a PDF."
                : "An invoice draft will be created in the accounting system. The customer will not be emailed automatically."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-gray-600">Customer:</span>
              <span className="font-medium">
                {booking.customer?.name || booking.customer?.full_name || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-gray-600">Invoice Email:</span>
              <span className="font-medium">
                {booking.customer?.company_profile?.invoice_email || booking.customer?.email || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-gray-600">Venue:</span>
              <span className="font-medium">
                {bookingDetails?.venueName || "N/A"}
              </span>
            </div>

            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-sm text-gray-600">Cost Center:</span>
              <span className="font-medium">
                {bookingDetails?.costCenter || (isRoadShow ? "N/A" : getCostCenterName())}
              </span>
            </div>

            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">
                Total Amount (excl. VAT):
              </span>
              <span className="font-semibold text-lg">
                {subtotalNet.toFixed(2)} {getCurrencySymbol(currency)}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmModal(false)}
              disabled={isGeneratingInvoice}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice}
            >
              {isGeneratingInvoice
                ? "Generating..."
                : pendingSendEmail
                  ? "Confirm, Generate & Send"
                  : "Confirm & Generate Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Invoice Confirmation Modal */}
      <Dialog open={showDisconnectModal} onOpenChange={setShowDisconnectModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Disconnect {accountingSystemLabel} Invoice</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to disconnect {accountingSystemLabel} invoice{" "}
              <span className="font-semibold">{invoiceData?.invoice_id}</span>?
            </p>

            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800">
                <span className="font-semibold">Warning:</span> This action will
                disconnect the current {accountingSystemLabel} invoice from this
                booking. You will be able to generate a new invoice afterward.
              </p>
            </div>

            <p className="text-sm text-gray-600">
              This action will be logged in the activity history.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisconnectModal(false)}
              disabled={isDisconnecting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisconnectInvoice}
              disabled={isDisconnecting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDisconnecting ? "Disconnecting..." : "Yes, Disconnect Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OAuth Re-authorization Dialog */}
      {venueId && (
        <OAuthReauthDialog
          open={showReauthDialog}
          onOpenChange={setShowReauthDialog}
          accountingSystem={detectedAccountingSystem}
          venueId={venueId}
        />
      )}

      {/* Edit Price Modal for Road Shows */}
      <Dialog open={showEditPriceModal} onOpenChange={setShowEditPriceModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Price</DialogTitle>
            <DialogDescription>
              Update the price for this road show booking. Prices are excluding VAT.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Base price — universal field, default 0 (doc §10.4) */}
            <div className="grid gap-2">
              <Label htmlFor="base-price">
                Base price ({getCurrencySymbol(currency)})
              </Label>
              <Input
                id="base-price"
                type="number"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                placeholder="0"
                min="0"
                step="100"
              />
            </div>

            {/* Per person rate × participant count (doc §7.5) */}
            <div className="grid gap-2">
              <Label htmlFor="price-excl-vat">
                Per person ({getCurrencySymbol(currency)})
              </Label>
              <Input
                id="price-excl-vat"
                type="number"
                value={priceExcludingVat}
                onChange={(e) => setPriceExcludingVat(e.target.value)}
                placeholder="0"
                min="0"
                step="100"
              />
              <p className="text-xs text-muted-foreground">
                × {rsParticipants} participant{rsParticipants === 1 ? "" : "s"} ={" "}
                {rsPerPersonTotal.toFixed(2)} {getCurrencySymbol(currency)}
              </p>
            </div>

            {/* Travel time + extended-staff line items (doc §7.5/§8.4) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="travel-cost">
                  Travel time ({getCurrencySymbol(currency)})
                </Label>
                <Input
                  id="travel-cost"
                  type="number"
                  value={travelCost}
                  onChange={(e) => setTravelCost(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="100"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="extra-staff-cost">
                  Extended staff ({getCurrencySymbol(currency)})
                </Label>
                <Input
                  id="extra-staff-cost"
                  type="number"
                  value={extraStaffCost}
                  onChange={(e) => setExtraStaffCost(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="100"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="vat-percent">
                VAT Percentage (%)
              </Label>
              <Input
                id="vat-percent"
                type="number"
                value={vatPercent}
                onChange={(e) => setVatPercent(e.target.value)}
                placeholder="Enter VAT percentage (e.g., 25)"
                min="0"
                max="100"
                step="0.1"
              />
            </div>

            <div className="grid gap-2">
              <Label>To pay</Label>
              <div className="p-3 bg-muted rounded-md space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base price</span>
                  <span>{rsBasePrice.toFixed(2)} {getCurrencySymbol(currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Per person {rsPerPersonRate.toFixed(0)} × {rsParticipants}
                  </span>
                  <span>{rsPerPersonTotal.toFixed(2)} {getCurrencySymbol(currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Travel time</span>
                  <span>{rsTravelCost.toFixed(2)} {getCurrencySymbol(currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Extended staff</span>
                  <span>{rsExtraStaffCost.toFixed(2)} {getCurrencySymbol(currency)}</span>
                </div>
                <div className="flex justify-between border-t pt-1">
                  <span className="text-muted-foreground">Subtotal (excl. VAT)</span>
                  <span>{rsSubtotalExclVat.toFixed(2)} {getCurrencySymbol(currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">VAT {rsVatPct}%</span>
                  <span>{rsVatAmount.toFixed(2)} {getCurrencySymbol(currency)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 text-base font-bold">
                  <span>Total</span>
                  <span>{rsTotalInclVat.toFixed(2)} {getCurrencySymbol(currency)}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditPriceModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (isNaN(rsTotalInclVat) || rsTotalInclVat < 0) {
                  toast.error("Please enter a valid price");
                  return;
                }

                setIsUpdating(true);
                try {
                  // For road shows, always use venue ID from URL
                  // Extract venue ID from current URL path
                  const pathMatch = window.location.pathname.match(/\/venue\/(\d+)\//);
                  const venueId = pathMatch ? pathMatch[1] : null;

                  if (!venueId) {
                    console.error("Could not extract venue ID from URL");
                    toast.error("Unable to update price - venue not found");
                    return;
                  }

                  console.log("Updating price for booking:", {
                    bookingId: booking.id,
                    venueId,
                    newPrice: rsTotalInclVat,
                    url: `/api/staff/venues/${venueId}/bookings/${booking.id}`
                  });

                  // Price components — base + travel + extended staff (excl. VAT).
                  // Travel/staff have no dedicated columns yet, so they're folded
                  // into the excl-VAT subtotal that is persisted (doc §7.5).
                  const priceExclVat = rsSubtotalExclVat;
                  const vatAmount = rsVatAmount;
                  const totalWithVat = rsTotalInclVat;

                  const response = await fetch(`/api/staff/venues/${venueId}/bookings/${booking.id}`, {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      // Net total excl VAT = base + per-person×participants (+ travel/staff)
                      totalPrice: priceExclVat,
                      base_amount_excl_vat: priceExclVat,
                      // Road-show split persisted separately: base fee + per-person
                      // rate + travel + extended-staff line items.
                      show_base_price: rsBasePrice,
                      per_person_mode_price_excluding_vat: rsPerPersonRate,
                      travel_cost_excl_vat: rsTravelCost,
                      extended_staff_cost_excl_vat: rsExtraStaffCost,
                      // Send VAT details separately
                      vat_rate: parseFloat(vatPercent), // Send as percentage (e.g., 25)
                      vat_amount: vatAmount,
                      // Total including VAT
                      total_price_incl_vat: totalWithVat,
                      // Price mode for road shows
                      priceMode: "fixed",
                      // Mark as road show update
                      bookingType: "roadshow",
                      isRoadShowUpdate: true,
                      skipPricingRecalculation: true,
                      currency: currency || "SEK"
                    }),
                  });

                  const contentType = response.headers.get("content-type");
                  if (!response.ok) {
                    let errorMessage = "Failed to update price";

                    if (contentType && contentType.includes("application/json")) {
                      try {
                        const errorData = await response.json();
                        console.error("Price update error:", errorData);
                        errorMessage = errorData.error || errorMessage;
                      } catch (e) {
                        console.error("Failed to parse error response");
                      }
                    } else {
                      console.error("Non-JSON error response:", response.status, response.statusText);
                    }

                    throw new Error(errorMessage);
                  }

                  toast.success("Price updated successfully");
                  setShowEditPriceModal(false);
                  if (onPaymentStatusUpdate) {
                    onPaymentStatusUpdate();
                  }
                } catch (error) {
                  console.error("Error updating price:", error);
                  toast.error("Failed to update price");
                } finally {
                  setIsUpdating(false);
                }
              }}
              disabled={isUpdating}
            >
              {isUpdating ? "Updating..." : "Update Price"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Invoice confirmation modal — mandatory note becomes a
          booking_notes row with category='manual_invoice'. Once confirmed,
          the booking is flagged and the auto-invoice job skips it. */}
      <Dialog
        open={manualInvoiceModalOpen}
        onOpenChange={(open) => {
          if (savingManualInvoice) return;
          setManualInvoiceModalOpen(open);
          if (!open) setManualInvoiceNoteDraft("");
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Mark for Manual Invoice</DialogTitle>
            <DialogDescription>
              This booking will be excluded from the auto-invoice job — an
              admin will issue the invoice manually. Please provide a
              reason; it will be saved as an internal note on this booking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="manual-invoice-note-staff">
                Reason / note <span className="text-red-500">*</span>
              </Label>
              <textarea
                id="manual-invoice-note-staff"
                value={manualInvoiceNoteDraft}
                onChange={(e) => setManualInvoiceNoteDraft(e.target.value)}
                placeholder="Why this booking requires a manual invoice…"
                rows={4}
                disabled={savingManualInvoice}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setManualInvoiceModalOpen(false);
                setManualInvoiceNoteDraft("");
              }}
              disabled={savingManualInvoice}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmManualInvoice}
              disabled={
                !manualInvoiceNoteDraft.trim() || savingManualInvoice
              }
            >
              {savingManualInvoice ? "Saving…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
